import React, { useState } from 'react';
import { Modal, message, Form } from 'antd';
import { FormInstance } from 'antd';
import { FeatureGroupType } from '../params/FeatureGroup';
import useCreateProjectPresetsMutation from '../../hooks/mutations/useCreateProjectPresetsMutation';
import ProjectPresetInputFields from './ProjectPresetInputFields';

type Props = {
  isModalVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  currentParamGroup: string | null;
  setCurrentParamGroup: (paramGroup: string | null) => void;
  featureGroup: FeatureGroupType;
  formInput: FormInstance;
};

const ProjectDefaultModal: React.FC<Props> = ({
  isModalVisible,
  setIsModalVisible,
  currentParamGroup,
  setCurrentParamGroup,
  featureGroup,
  formInput,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { createProjectPresets } = useCreateProjectPresetsMutation();

  const handleCancel = () => {
    setIsModalVisible(false);
    setCurrentParamGroup(null);
    form.resetFields();
  };

  const handleSubmit = async (values: Record<string, string>) => {
    try {
      setLoading(true);

      const projectName = values['name'];

      const params = Object.keys(values)
        .filter((key) => key !== currentParamGroup && key !== 'name')
        .map((key) => {
          const param = featureGroup[currentParamGroup ?? '']?.find(
            (param) => param.name === key,
          );
          return {
            name: key,
            value: values[key],
            isSecret: param?.osep_generator_ui_is_secret ?? true,
          };
        });

      await createProjectPresets({
        name: projectName,
        type: currentParamGroup ?? '',
        params,
      });

      const mappedData = params.reduce(
        (acc, param) => {
          acc[param.name] = param.value;
          return acc;
        },
        {} as Record<string, any>,
      );

      formInput.setFieldsValue({
        ...mappedData,
      });

      message.success(
        'We will create your project and notify you via email once it is ready.',
      );
      form.resetFields();
      setIsModalVisible(false);
      setCurrentParamGroup(null);
    } catch (error) {
      message.error(
        'There was an error while creating the project. Please try again or contact the OSEP team.',
      );
      console.error('Error creating project presets:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Project Presets"
      open={isModalVisible}
      onCancel={handleCancel}
      okText="Create"
      onOk={() => form.submit()}
      confirmLoading={loading}
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        {currentParamGroup && (
          <ProjectPresetInputFields
            params={featureGroup[currentParamGroup]}
            paramGroupName={currentParamGroup}
          />
        )}
      </Form>
    </Modal>
  );
};

export default ProjectDefaultModal;
